/**
 * Lightbox de imagem em `document.body` (fora do card do chat, que usa `contain:` no CSS).
 *
 * Quando o chat está em iframe, o iframe envia `postMessage` — ver contrato no `ChatbotWindow.vue`.
 * A página **pai** deve implementar o `window.addEventListener('message', …)` (não trata-se aqui).
 */
const HOST_ID = 'og-chatbot-image-lightbox-host';
const STYLE_ID = 'og-chatbot-lightbox-inject-style';

const CSS = `
#${HOST_ID}{position:fixed;inset:0;z-index:2147483640;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;background:rgba(0,0,0,.88);font-family:system-ui,-apple-system,sans-serif}
#${HOST_ID} .og-chatbot-plb-close{position:absolute;top:12px;right:12px;width:44px;height:44px;border:none;border-radius:8px;background:rgba(255,255,255,.12);color:#fff;font-size:26px;line-height:1;cursor:pointer;z-index:1;display:flex;align-items:center;justify-content:center;padding:0}
#${HOST_ID} .og-chatbot-plb-close:hover{background:rgba(255,255,255,.22)}
#${HOST_ID} .og-chatbot-plb-img{max-width:100%;max-height:calc(100vh - 48px);width:auto;height:auto;object-fit:contain;border-radius:8px;box-shadow:0 8px 40px rgba(0,0,0,.5)}`;

/**
 * @param {Document} targetDoc
 * @param {Window} targetWin
 * @param {string} url
 * @param {object} [opts]
 * @param {() => void} [opts.onClose] chamado após o usuário fechar
 * @returns {{ destroy: () => void } | null}
 */
export function mountLightboxInDocument(targetDoc, targetWin, url, opts = {}) {
  if (!targetDoc || !targetWin || !url) return null;

  const old = targetDoc.getElementById(HOST_ID);
  if (old) old.remove();

  if (!targetDoc.getElementById(STYLE_ID)) {
    const style = targetDoc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    targetDoc.head.appendChild(style);
  }

  const host = targetDoc.createElement('div');
  host.id = HOST_ID;
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', 'Visualização da imagem');

  const btn = targetDoc.createElement('button');
  btn.type = 'button';
  btn.className = 'og-chatbot-plb-close';
  btn.setAttribute('aria-label', 'Fechar');
  btn.appendChild(targetDoc.createTextNode('\u00d7'));

  const img = targetDoc.createElement('img');
  img.className = 'og-chatbot-plb-img';
  img.alt = '';
  img.src = url;

  let onKeydownHandler = null;
  const close = () => {
    if (host.parentNode) host.remove();
    try {
      targetDoc.body.style.overflow = '';
    } catch (e) {
      /* */
    }
    if (onKeydownHandler) {
      targetWin.removeEventListener('keydown', onKeydownHandler);
    }
    onKeydownHandler = null;
    if (typeof opts.onClose === 'function') {
      try {
        opts.onClose();
      } catch (e) {
        /* */
      }
    }
  };

  onKeydownHandler = (ev) => {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      close();
    }
  };
  targetWin.addEventListener('keydown', onKeydownHandler);

  btn.addEventListener('click', (ev) => {
    ev.stopPropagation();
    close();
  });
  host.addEventListener('click', (ev) => {
    if (ev.target === host) close();
  });
  img.addEventListener('click', (ev) => ev.stopPropagation());

  host.appendChild(btn);
  host.appendChild(img);
  try {
    targetDoc.body.style.overflow = 'hidden';
  } catch (e) {
    /* */
  }
  targetDoc.body.appendChild(host);

  return { destroy: close };
}

/**
 * Fecha o host no documento, se existir, sem reexecutar onClose.
 */
export function unmountLightboxInDocument(targetDoc) {
  if (!targetDoc) return;
  const host = targetDoc.getElementById(HOST_ID);
  if (host) host.remove();
  try {
    targetDoc.body.style.overflow = '';
  } catch (e) {
    /* */
  }
}
